import React, { useState, useEffect, useMemo } from 'react';
import styles from '../styles/dialogue.module.css';
import Layout from '../components/Layout';
import layoutConfig from 'components/DashboardBhima/layoutConfig';
import GridRenderer from 'components/DashboardBhima/GridRenderer';
import { BhimaBulkDataProvider, useBhimaBulkData } from 'components/DashboardBhima/BhimaBulkDataContext';
import { buildBulkFilterPayload } from 'components/DashboardBhima/BhimaDashboardFilters';
import { getRoleTemplates, getTemplates } from 'api/admin/index';
import loadingGif from 'images/loader2.webp';

const MAX_COLUMNS = 24;

const bulkLoaderStyle = {
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const bulkLoaderContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
};

function DashboardBhimaContent({ config, filter, onFilterChange }) {
    const { loading } = useBhimaBulkData();
    if (loading) {
        return (
            <div className={styles.newDialog} style={bulkLoaderStyle}>
                <div style={bulkLoaderContentStyle}>
                    <img src={loadingGif} alt="Loading" style={{ height: '48px', width: '48px' }} />
                    <span style={{ fontFamily: 'Cinzel, serif', color: 'white' }}>Loading dashboard…</span>
                </div>
            </div>
        );
    }
    return (
        <div className={styles.newDialog}>
            <GridRenderer config={config} filter={filter} onFilterChange={onFilterChange} />
        </div>
    );
}

const DEFAULT_FILTER = {
    startDate: '',
    endDate: '',
    branch: '',
    schemeType: 'all'
};

const DashboardBhima = () => {
    const [config, setConfig] = useState(layoutConfig);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(DEFAULT_FILTER);

    const filterPayload = useMemo(() => buildBulkFilterPayload(filter), [filter]);

    useEffect(() => {
        const roleId = localStorage.getItem('roleId');
        if (!roleId) {
            setConfig(layoutConfig);
            setLoading(false);
            return;
        }
        // here if it is admin then need to call the getTemplates api for the listing if it is not admin then call this roleTemplates api
        const isAdmin = localStorage.getItem('isAdmin');
        if (isAdmin) {
            getTemplates()
                .then((response) => {
                    const templates = response?.data;
                    setConfig({
                        maxColumns: MAX_COLUMNS,
                        sections: templates[0].templateJson
                    });
                })
                .catch(() => {
                    setConfig(layoutConfig);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            getRoleTemplates(roleId)
                .then((response) => {
                    const templates = response?.data;
                    const hasSections = Array.isArray(templates) &&
                        templates.length > 0 &&
                        Array.isArray(templates[0]?.templateJson) &&
                        templates[0].templateJson.length > 0;
                    if (hasSections) {
                        setConfig({
                            maxColumns: MAX_COLUMNS,
                            sections: templates[0].templateJson
                        });
                    } else {
                        setConfig(layoutConfig);
                    }
                })
                .catch(() => {
                    setConfig(layoutConfig);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, []);

    if (loading) {
        return (
            <Layout hasDashboardFilter>
                <div className={styles.newDialog} style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                        <img src={loadingGif} alt="Loading" style={{ height: '48px', width: '48px' }} />
                        <span style={{ fontFamily: 'Cinzel, serif', color: 'white' }}>Loading dashboard…</span>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout hasDashboardFilter>
            <BhimaBulkDataProvider config={config} filter={filterPayload}>
                <DashboardBhimaContent
                    config={config}
                    filter={filter}
                    onFilterChange={setFilter}
                />
            </BhimaBulkDataProvider>
        </Layout>
    );
}

export default DashboardBhima;
